/**
 * Scale Drop Modal – Bulk edit mode (3-column layout).
 * Config | Creative (Senders + Texts) | Leads.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Settings2, Users } from 'lucide-react';
import { toClickIdStructure, toDomainScheme } from '@trafficpulse/shared';

import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';

import { useGatewayPerformanceTags, useLeadsUsage, useListPerformanceTags, useTextPerformanceTags } from '@/hooks';
import { LeadsSection } from '@/features/smswiser-campaigns/components/prepared-drop-modals/sections/LeadsSection';
import { useBulkEditPreparedDropsForm } from '@/features/smswiser-campaigns/components/prepared-drop-modals/useBulkEditPreparedDropsForm';
import { ScaleDropModalLayout } from '../ScaleDropModalLayout';
import { ColumnHeader } from '../shared';
import { ScaleDropModalBulkEditFooter } from './ScaleDropModalBulkEditFooter';
import { ScaleDropModalBulkEditTopLeft } from './ScaleDropModalBulkEditTopLeft';
import { BulkEditCreativeColumn } from './BulkEditCreativeColumn';
import { useScaleDropEnrichment } from '../hooks/useScaleDropEnrichment';
import { mergeBulkEditDrops } from '../utils/mergeBulkEditDrops';
import type { ScaleDropModalProps } from '../ScaleDropModal.types';

export function ScaleDropModalBulkEdit(props: ScaleDropModalProps) {
  const { countryKey, isBusy, onClose, editDropIds = [], editDrops, gateways, campaigns = [], tlds, leadsLists, senderNames, smsTexts, smsTextsWithIds } = props;
  const [isSaveAndSendSubmitting, setIsSaveAndSendSubmitting] = useState(false);
  const scheduleIntervalMinutesRef = useRef(0);
  const usageQuery = useLeadsUsage(countryKey, props.defaultWorkDate);
  const internalTagsQuery = useListPerformanceTags(countryKey, undefined, undefined, !props.perfTagsQuery);
  const perfTagsQuery = props.perfTagsQuery ?? internalTagsQuery;
  const wiserIds = useMemo(() => {
    if (props.wiserIds?.length) return props.wiserIds;
    const fromEdit = props.editDrops?.map((d) => d.wiserId).filter(Number.isFinite) ?? [];
    return fromEdit.length ? [...new Set(fromEdit)] : [];
  }, [props.wiserIds, props.editDrops]);
  const textPerfTagsQuery = useTextPerformanceTags(countryKey, wiserIds.length > 0 ? wiserIds : undefined, null, true);
  const gatewayPerfTagsQuery = useGatewayPerformanceTags(countryKey, wiserIds.length > 0 ? wiserIds : undefined, null, true);
  const { enrichedLeads, tagFilterOptions } = useScaleDropEnrichment(perfTagsQuery, leadsLists);
  const usageByListId = usageQuery.data ?? {};
  const bulkCount = editDropIds.length;

  const dropLabels = useMemo((): DropLabel[] | undefined => {
    if (bulkCount === 0 || !editDrops?.length || editDropIds.length !== editDrops.length) return undefined;
    return editDropIds.map((id, i) => {
      const ed = editDrops[i];
      const listName = ed ? leadsLists.find((l) => l.id === ed.leadsListWiserId)?.name : undefined;
      return { id, label: listName ?? `Draft ${i + 1}` };
    });
  }, [bulkCount, editDropIds, editDrops, leadsLists]);

  const { initialValues, mixedOptions } = useMemo(
    () =>
      bulkCount === 1 && editDrops?.[0]
        ? {
            initialValues: {
              routeId: editDrops[0].routeId, wiserId: editDrops[0].wiserId,
              shortLinkTldValue: editDrops[0].shortLinkTldValue, amount: editDrops[0].amount,
              leadsListWiserId: editDrops[0].leadsListWiserId, executeAfter: editDrops[0].executeAfter ?? null,
              senderNames: editDrops[0].senderNames ?? [], smsTexts: editDrops[0].smsTexts ?? [],
              batchAmount: editDrops[0].batchAmount ?? null, batchDelay: editDrops[0].batchDelay ?? null,
              forceSend: editDrops[0].forceSend ?? false,
              aiOptimizationEnabled: editDrops[0].aiOptimizationEnabled ?? false,
              aiOptimizationType: editDrops[0].aiOptimizationType ?? null,
              aiOptimizationBatchAmount: editDrops[0].aiOptimizationBatchAmount ?? null,
              aiOptimizationMinimalValuePercent: editDrops[0].aiOptimizationMinimalValuePercent ?? null,
              aiOptimizationSendUntil: editDrops[0].aiOptimizationSendUntil ?? null,
              useOnlyNewDomains: editDrops[0].useOnlyNewDomains ?? false,
              clickIdStructure: toClickIdStructure(editDrops[0].clickIdStructure),
              domainScheme: toDomainScheme(editDrops[0].domainScheme),
              staticTagId: editDrops[0].staticTagId ?? null,
            },
            mixedOptions: undefined as undefined,
          }
        : mergeBulkEditDrops(editDrops, bulkCount),
    [bulkCount, editDrops],
  );

  const handleSavePayload = useCallback(
    async (payload: Parameters<NonNullable<typeof props.onBulkSave>>[1]) => {
      const p = payload as { smsTextsPerDrop?: string[][]; senderNamesPerDrop?: string[][] };
      const perDropTexts = p.smsTextsPerDrop;
      const perDropSenders = p.senderNamesPerDrop;
      const needsPerItem =
        (perDropTexts?.length && editDropIds.length >= perDropTexts.length) ||
        (perDropSenders?.length && editDropIds.length >= perDropSenders.length);
      if (needsPerItem && props.onBulkSave) {
        const { smsTextsPerDrop: _t, senderNamesPerDrop: _s, ...rest } = p;
        for (let i = 0; i < editDropIds.length; i++) {
          const itemPayload = { ...rest } as Parameters<NonNullable<typeof props.onBulkSave>>[1];
          if (perDropTexts?.[i]?.length) (itemPayload as { smsTexts?: string[] }).smsTexts = perDropTexts[i];
          if (perDropSenders?.[i]?.length) (itemPayload as { senderNames?: string[] }).senderNames = perDropSenders[i];
          await props.onBulkSave!([editDropIds[i]], itemPayload, scheduleIntervalMinutesRef.current || undefined);
        }
      } else {
        const copy = { ...payload } as Record<string, unknown>;
        delete copy.smsTextsPerDrop;
        delete copy.senderNamesPerDrop;
        await props.onBulkSave?.(editDropIds, copy as typeof payload, scheduleIntervalMinutesRef.current || undefined);
      }
      onClose();
    },
    [editDropIds, onClose, props],
  );

  const bulkForm = useBulkEditPreparedDropsForm(
    countryKey, bulkCount,
    gateways.map((g) => ({ id: g.id, name: g.name, pricePerSms: g.pricePerSms })),
    campaigns ?? [],
    leadsLists.map((l) => ({ id: l.id, name: l.name, count: l.count })),
    senderNames, smsTexts,
    handleSavePayload,
    onClose, props.onAddSender, props.onAddText, props.onDeleteSender, props.onDeleteText,
    initialValues, mixedOptions,
    props.onBulkSaveAndSend
      ? (payload) => props.onBulkSaveAndSend!(editDropIds, payload, scheduleIntervalMinutesRef.current || undefined)
      : undefined,
  );

  useEffect(() => { scheduleIntervalMinutesRef.current = bulkForm.scheduleIntervalMinutes; });

  if (bulkCount === 0) return null;

  const hidePending = async () => {
    if (bulkForm.pendingHideTexts.length > 0 && props.onDeleteTextsBulk) {
      await props.onDeleteTextsBulk(bulkForm.pendingHideTexts);
    }
    if (bulkForm.pendingHideSenders.length > 0) {
      for (const name of bulkForm.pendingHideSenders) await props.onDeleteSender?.(name);
    }
  };

  const handleSaveClick = async () => {
    await bulkForm.handleSave();
    await hidePending();
    onClose();
  };
  const handleSaveAndSendClick = async () => {
    setIsSaveAndSendSubmitting(true);
    try {
      await bulkForm.handleSaveAndSend();
      await hidePending();
      onClose();
    } finally {
      setIsSaveAndSendSubmitting(false);
    }
  };

  const showingLoader = isBusy || isSaveAndSendSubmitting;

  return (
    <ScaleDropModalLayout
      title={bulkCount === 1 ? 'Edit draft' : `Bulk edit — ${bulkCount} drafts`}
      icon={<Settings2 className="h-5 w-5" />}
      onClose={onClose}
      isBusy={showingLoader}
      footer={
        <ScaleDropModalBulkEditFooter
          onClose={onClose} showingLoader={showingLoader} onSave={handleSaveClick}
          onSaveAndSend={props.onBulkSaveAndSend ? handleSaveAndSendClick : undefined}
          hasAnyChange={bulkForm.hasAnyChange} bulkCount={bulkCount} isSaveAndSendSubmitting={isSaveAndSendSubmitting}
          aiSettingsIncomplete={bulkForm.aiSettingsIncomplete}
        />
      }
      configSection={
        <ScaleDropModalBulkEditTopLeft
          bulkCount={bulkCount} gateways={gateways} campaigns={campaigns ?? []} tlds={tlds} countryKey={countryKey}
          tagsByGatewayId={gatewayPerfTagsQuery.data?.tagsByGatewayId} tagColors={gatewayPerfTagsQuery.data?.tagColors}
          bulkForm={bulkForm} isBusy={isBusy}
        />
      }
      creativeSection={
        <BulkEditCreativeColumn
          bulkForm={bulkForm}
          isBusy={isBusy}
          countryKey={countryKey}
          dropCount={bulkCount}
          editDropIds={editDropIds}
          dropLabels={dropLabels}
          onAddSender={props.onAddSender}
          onAddText={props.onAddText}
          onDeleteTextsBulk={props.onDeleteTextsBulk}
          onBulkSave={props.onBulkSave}
          onClose={onClose}
          onHideTextsAfterUseAndHide={
            props.onDeleteTextsBulk
              ? (texts) => props.onDeleteTextsBulk!(texts)
              : undefined
          }
          tagsByTextId={textPerfTagsQuery.data?.tagsByTextId}
          tagColors={textPerfTagsQuery.data?.tagColors}
          smsTextsWithIds={smsTextsWithIds}
        />
      }
      leadsSection={
        enrichedLeads.length > 0 ? (
          <LeadsSection
            leadsLists={enrichedLeads.map((l) => ({ id: l.id, name: l.name, count: l.count, leadCountFetchedAt: l.leadCountFetchedAt, isNew: l.isNew, neverBeenUsed: l.neverBeenUsed, performanceTags: l.performanceTags }))}
            tagColors={perfTagsQuery.data?.tagColors} tagsLoading={perfTagsQuery.isLoading} tagFilterOptions={tagFilterOptions}
            selectedId={bulkForm.leadsListWiserId} onSelect={bulkForm.setLeadsListWiserId}
            search={bulkForm.leadSearch} onSearch={bulkForm.setLeadSearch}
            showLeads={bulkForm.showLeads} onToggleShow={() => bulkForm.setShowLeads((v: boolean) => !v)}
            allowUnchanged usageByListId={usageByListId}
          />
        ) : (
          <>
            <ColumnHeader icon={Users} title="Leads" subtitle="Select target list" />
            <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
              No leads lists available
            </div>
          </>
        )
      }
    />
  );
}
